import React, { useState, useEffect, useRef, useContext } from 'react'
import { Toast } from 'primereact/toast'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { Row, Col, Card, CardBody, CardTitle } from 'reactstrap'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import SearchResults from './SearchResults'
import MyOrders from './MyOrders'
import OrderCartSummary from './OrderCartSummary'
import server from '../../server'
import {
  shouldConfirmHighQuantity,
  openHighQuantityConfirmDialog,
} from './highQuantityOrder'
import {
  getMaxOrderQuantityPerOrder,
  normalizeLineQty,
  openMaxOrderPerOrderDialog,
} from './maxOrderPerOrder'
import {
  getMinOrderQuantity,
  openMinOrderPerOrderDialog,
} from './minOrderPerOrder'
import { ToastPopUp } from '../shared/utils'
import {
  getDraftOrder,
  saveDraftOrder,
  clearDraftOrder,
} from './draftOrderApi'
import { AuthenticationContext } from '../../Auth/authentication.context'
import useTableRowsPerPage from '../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../shared/tablePagination'

const AddOrders = () => {
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()
  const toastRef = useRef(null)
  // Tracks whether the persisted draft has finished loading, so hydration does
  // not immediately re-save it. Holds the debounce timer for server saves.
  const draftLoadedRef = useRef(false)
  const draftSaveTimerRef = useRef(null)
  const [searchVal, setSearchVal] = useState('')
  const [tableLoading, setTableLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [myOrder, setMyOrder] = useState([])
  const [orderSummaryVisible, setOrderSummaryVisible] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [state, setState] = useState(() => getInitialPaginationState(user))

  useEffect(() => {
    setState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  // Load the draft order. Paint instantly from the localStorage cache, then
  // reconcile with the server (the source of truth), so the cart follows the
  // user across logout, cache clearing and switching devices.
  useEffect(() => {
    const savedMyOrder = localStorage.getItem('myOrder')
    if (savedMyOrder) {
      try {
        setMyOrder(JSON.parse(savedMyOrder))
      } catch (e) {
        console.error('Failed to parse cached draft order:', e)
      }
    }

    let cancelled = false
    const loadDraft = async () => {
      try {
        const items = await getDraftOrder()
        if (!cancelled) {
          setMyOrder(items)
          localStorage.setItem('myOrder', JSON.stringify(items))
        }
      } catch (e) {
        // Fall back to the localStorage cache when the server is unreachable.
        console.error('Failed to load draft order from server:', e)
      } finally {
        if (!cancelled) {
          draftLoadedRef.current = true
        }
      }
    }
    loadDraft()

    return () => {
      cancelled = true
    }
  }, [])

  // Mirror every cart change into the localStorage cache immediately, and
  // persist to the server on a short debounce (once the initial draft has
  // loaded) so we don't POST on every keystroke or re-save the loaded draft.
  useEffect(() => {
    localStorage.setItem('myOrder', JSON.stringify(myOrder))

    if (!draftLoadedRef.current) {
      return
    }

    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current)
    }
    const itemsToSave = myOrder
    draftSaveTimerRef.current = setTimeout(() => {
      saveDraftOrder(itemsToSave).catch((e) => {
        // Fail soft: the localStorage cache still holds the cart.
        console.error('Failed to save draft order to server:', e)
      })
    }, 600)

    return () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current)
      }
    }
  }, [myOrder])

  useEffect(() => {
    if (searchVal !== '') {
      handleFetchData()
    }
  }, [searchVal, state])

  const sameId = (a, b) => a != null && b != null && String(a) === String(b)

  const validateTempQuantityForOrder = (itemData) => {
    if (!itemData.tempQuantity) {
      return false
    }
    const minQty = getMinOrderQuantity(itemData)
    if (minQty != null && itemData.tempQuantity < minQty) {
      return false
    }
    if (
      itemData.multipleOrderQuantity > 0 &&
      itemData.tempQuantity % itemData.multipleOrderQuantity !== 0
    ) {
      return false
    }
    return true
  }

  const editItem = (editedItem) => {
    const lineQty = normalizeLineQty(
      editedItem.quantity ?? editedItem.tempQuantity
    )
    const maxCap = getMaxOrderQuantityPerOrder(editedItem)
    if (maxCap != null && Number.isFinite(lineQty) && lineQty > maxCap) {
      ToastPopUp(
        toastRef,
        'warn',
        'Maximum quantity',
        `This product allows at most ${maxCap} units per order line.`
      )
      return
    }

    const normalizedLine = {
      ...editedItem,
      quantity: Number.isFinite(lineQty) ? lineQty : editedItem.quantity,
      tempQuantity: Number.isFinite(lineQty)
        ? lineQty
        : editedItem.tempQuantity ?? editedItem.quantity,
    }

    const MyOrderIndex = myOrder.findIndex((item) =>
      sameId(item.id, normalizedLine.id)
    )
    if (MyOrderIndex !== -1) {
      const updatedOrderData = [...myOrder]
      updatedOrderData[MyOrderIndex] = normalizedLine
      setMyOrder(updatedOrderData)
    }
    const MyProductIndex = products.findIndex((product) =>
      sameId(product.id, normalizedLine.id)
    )
    if (MyProductIndex !== -1) {
      const updatedProducts = [...products]
      updatedProducts[MyProductIndex] = normalizedLine
      setProducts(updatedProducts)
    }
  }

  const handleMyOrder = (itemData) => {
    if (itemData.backlog) {
      ToastPopUp(
        toastRef,
        'warn',
        'Backorder',
        'This item is on backorder and cannot be added to your order.'
      )
      return
    }
    if (!validateTempQuantityForOrder(itemData)) {
      const minPerOrder = getMinOrderQuantity(itemData)
      const tq = normalizeLineQty(itemData.tempQuantity)
      if (minPerOrder != null && Number.isFinite(tq) && tq < minPerOrder) {
        ToastPopUp(
          toastRef,
          'warn',
          'Minimum quantity',
          `This product requires at least ${minPerOrder} units per order line. Quantity was not updated.`
        )
      }
      return
    }

    const maxCap = getMaxOrderQuantityPerOrder(itemData)
    const tq = normalizeLineQty(itemData.tempQuantity)
    if (maxCap != null && Number.isFinite(tq) && tq > maxCap) {
      ToastPopUp(
        toastRef,
        'warn',
        'Maximum quantity',
        `This product allows at most ${maxCap} units per order line. Quantity was not updated.`
      )
      return
    }

    const orderItem = {
      ...itemData,
      quantity: itemData.tempQuantity,
      subtotal: itemData.tempQuantity * itemData.sellingPrice,
    }

    const index = products.findIndex((product) => sameId(product.id, itemData.id))
    const updatedProducts = [...products]
    if (index !== -1) {
      updatedProducts[index] = orderItem
      setProducts(updatedProducts)
    }

    // Check if item already exists in myOrder
    const existingOrderIndex = myOrder.findIndex((item) => sameId(item.id, itemData.id))
    if (existingOrderIndex === -1) {
      setMyOrder((prevMyOrder) => [...prevMyOrder, orderItem])
    }
  }

  const handleDeleteItem = (itemData) => {
    const sameId = (a, b) => a != null && b != null && String(a) === String(b)

    const { quantity, ...productItem } = itemData
    const index = products.findIndex((product) => sameId(product.id, itemData.id))
    if (index !== -1) {
      const updatedProducts = [...products]
      updatedProducts[index] = productItem
      setProducts(updatedProducts)
    }
    setMyOrder((prevMyOrder) =>
      prevMyOrder.filter((item) => !sameId(item.id, itemData.id))
    )
  }

  const handleMyOrderWithHighQuantityConfirm = (itemData) => {
    if (itemData.backlog) {
      ToastPopUp(
        toastRef,
        'warn',
        'Backorder',
        'This item is on backorder and cannot be added to your order.'
      )
      return
    }
    const qty = normalizeLineQty(itemData.tempQuantity)
    const minPerOrder = getMinOrderQuantity(itemData)
    if (minPerOrder != null && Number.isFinite(qty) && qty < minPerOrder) {
      openMinOrderPerOrderDialog({
        itemLabel:
          itemData.itemDescription || itemData.retailUpcCode || 'This product',
        requestedQty: qty,
        minQty: minPerOrder,
        onUseMin: () => {
          handleMyOrderWithHighQuantityConfirm({
            ...itemData,
            tempQuantity: minPerOrder,
          })
        },
      })
      return
    }

    if (!validateTempQuantityForOrder(itemData)) {
      return
    }

    const maxPerOrder = getMaxOrderQuantityPerOrder(itemData)
    if (maxPerOrder != null && Number.isFinite(qty) && qty > maxPerOrder) {
      openMaxOrderPerOrderDialog({
        itemLabel:
          itemData.itemDescription || itemData.retailUpcCode || 'This product',
        requestedQty: qty,
        maxQty: maxPerOrder,
        onUseMax: () => {
          const capped = { ...itemData, tempQuantity: maxPerOrder }
          if (!validateTempQuantityForOrder(capped)) {
            ToastPopUp(
              toastRef,
              'warn',
              'Cannot add to order',
              'The maximum per order for this product is lower than the minimum order quantity. Adjust the quantity or catalogue limits.'
            )
            return
          }
          handleMyOrderWithHighQuantityConfirm(capped)
        },
      })
      return
    }

    const existingOrderIndex = myOrder.findIndex((item) => sameId(item.id, itemData.id))
    const isNewLine = existingOrderIndex === -1

    const run = () => handleMyOrder(itemData)

    if (
      !isNewLine ||
      !shouldConfirmHighQuantity({
        newQuantity: qty,
        originalLineQuantity: null,
      })
    ) {
      run()
      return
    }

    openHighQuantityConfirmDialog({ quantity: qty, onAccept: run })
  }

  // Reset the page after an order. `remaining` holds items that were not ordered
  // (manufacturer below its minimum) and stay in the draft.
  const clear = (ordableProducts, remaining = []) => {
    setSearchVal('')
    setProducts([])
    setMyOrder(remaining)
    setTotalCount([])
    setState({ first: 0, rows: defaultRows, page: 0 })
    if (remaining.length > 0) {
      saveDraftOrder(remaining).catch((e) => {
        console.error('Failed to save draft order to server:', e)
      })
      return
    }
    // Also empty the persisted draft on the server.
    clearDraftOrder().catch((e) => {
      console.error('Failed to clear draft order on server:', e)
    })
  }

  const handleFetchData = () => {
    if (searchVal !== '') {
      setTableLoading(true)
      
      // Normalize search query: if it's all digits, strip leading zeros
      // This allows searching DIN numbers with or without leading zeros
      let normalizedQuery = searchVal
      const trimmedSearch = searchVal.trim()
      if (/^\d+$/.test(trimmedSearch)) {
        // If the search is all digits, remove leading zeros
        // Handle edge case where search is all zeros (e.g., "0000")
        const parsed = parseInt(trimmedSearch, 10)
        normalizedQuery = isNaN(parsed) ? trimmedSearch : String(parsed)
      }
      
      server
        .get(`/products/`, {
          params: {
            q: normalizedQuery,
            page: state.page + 1,
            limit: state.rows,
          },
        })
        .then((response) => {
          const updatedResponse = response.data.data.map((product) => {
            const orderItem = myOrder.find((item) => sameId(item.id, product.id))
            if (!orderItem) {
              return product
            }
            // Keep catalogue limits from API row; cart line may omit fields (e.g. older localStorage).
            return {
              ...product,
              quantity: orderItem.quantity,
              tempQuantity: orderItem.quantity,
              subtotal: orderItem.subtotal,
            }
          })
          setProducts(updatedResponse)
          setTotalCount(response.data.totalCount || 0)
          setTableLoading(false)
      
        })
        .catch((error) => {
          setTableLoading(false)
          setTotalCount(0)
          console.error('Error fetching products:', error)
          
          // Enhanced error handling for product search
          let errorMessage = 'Unable to search products'
          
          if (error.response && error.response.data) {
            if (error.response.data.message) {
              errorMessage = error.response.data.message
            } else if (error.response.status === 401) {
              errorMessage = 'Please log in again to search products'
            } else if (error.response.status === 403) {
              errorMessage = 'You do not have permission to search products'
            } else if (error.response.status >= 500) {
              errorMessage = 'Search service is temporarily unavailable'
            }
          } else if (error.request) {
            errorMessage = 'Unable to connect to search service. Please check your internet connection.'
          }
          
          // You could add a toast notification here if needed
          // ToastPopUp(toast, 'error', 'Search Error', errorMessage)
        })
    }
  }

  const handleSearch = (e) => {
    setState((prev) => ({ ...prev, page: 0, first: 0 }))
    handleFetchData()
  }

  const handleResultsUpdate = (updatedResults) => {
    setProducts(updatedResults)
  }

  const handleKeyPress = (e, product) => {
    if (
      e.key === 'Enter' &&
      product.tempQuantity &&
      product.orderable !== false &&
      !product.backlog
    ) {
      e.preventDefault()
      handleMyOrderWithHighQuantityConfirm({
        ...product,
        tempQuantity: product.tempQuantity,
      })
    }
  }

  const handleClearSearch = () => {
    setSearchVal('')
    setProducts([])
    setTotalCount(0)
    setState({ first: 0, rows: defaultRows, page: 0 })
  }

  const calculateOrderTotal = () =>
    myOrder.reduce((total, order) => {
      if (Array.isArray(order.subtotal)) {
        return total + order.subtotal.reduce((acc, curr) => acc + curr, 0)
      }
      return total + (order.subtotal || 0)
    }, 0)

  const scrollToOrderTable = () => {
    document.getElementById('my-order-section')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  return (
    <React.Fragment>
      <ConfirmDialog />
      <div className='card flex justify-content-center'>
        <Toast ref={toastRef} />
      </div>
      <div className='page-content '>
        <div className='container-fluid'>
          <h3 className='fs-3 fw-bold mb-4'>Generics Order Pad</h3>
          {myOrder.length > 0 && !orderSummaryVisible && (
            <OrderCartSummary
              total={calculateOrderTotal()}
              itemCount={myOrder.length}
              onViewOrder={scrollToOrderTable}
            />
          )}
          <Row>
            <Col lg='12'>
              <Card>
                <CardBody>
                  <CardTitle tag='h5'>Search</CardTitle>
                  <form
                    onSubmit={(e) => {
                      handleSearch()
                      e.preventDefault()
                    }}
                  >
                    <div className='flex justify-content-end w-100'>
                      <div className='d-flex gap-2'>
                        <span className='p-input-icon-left w-100 my-auto'>
                          <i className='pi pi-search' />
                          <InputText
                            placeholder='search by UPC, DIN, Name or Manufacturer'
                            className='w-100'
                            value={searchVal}
                            onChange={(e) => setSearchVal(e.target.value)}
                          />
                        </span>
                        <Button
                          type='submit'
                          label='Search'
                          style={{ minWidth: '100px' }}
                        />
                        <Button
                          type='button'
                          label='Clear'
                          severity='secondary'
                          outlined
                          style={{ minWidth: '100px' }}
                          onClick={handleClearSearch}
                        />
                      </div>
                    </div>
                  </form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <h3 className='fs-3 fw-bold  mb-4'>Search Results</h3>
          <Row className='mb-3'>
            <Col lg='12'>
              <SearchResults
                results={products}
                lazyState={state}
                rowsPerPageOptions={rowsPerPageOptions}
                onLazyPage={(e) => {
                  setState((prev) => applyPageEvent(prev, e))
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
                totalCount={totalCount}
                handleMyOrder={handleMyOrderWithHighQuantityConfirm}
                tableLoading={tableLoading}
                editItem={editItem}
                handleResultsUpdate={handleResultsUpdate}
                handleKeyPress={handleKeyPress}
              />
            </Col>
          </Row>

          <div id='my-order-section'>
            <h3 className='fs-3 fw-bold mb-4'>My Order</h3>
            <Row>
              <Col lg='12'>
                <Card>
                  <MyOrders
                    myOrder={myOrder}
                    handleDeleteItem={handleDeleteItem}
                    clear={clear}
                    editItem={editItem}
                    handleMyOrder={handleMyOrderWithHighQuantityConfirm}
                    catalogueProducts={products}
                    orderSummaryVisible={orderSummaryVisible}
                    setOrderSummaryVisible={setOrderSummaryVisible}
                  />
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default AddOrders
